import { MovementType } from "@/data/movementType";

/**
 * Type for the category option structure
 */
export type CategoryOption = {
  section_name: string;
  fields: string[];
};

/**
 * Default category options used across the application
 * Synchronized with A5TransactionConcern::Enums from Ruby backend
 */
export const DEFAULT_CATEGORY_OPTIONS: CategoryOption[] = [
  {
    section_name: "other",
    fields: [MovementType.ASSET_LIQUIDATION, MovementType.AMORTIZATION],
  },
  {
    section_name: "account_fees",
    fields: [MovementType.FEES],
  },
  {
    section_name: "loan_flows",
    fields: [
      MovementType.HOME_LOAN,
      MovementType.OTHER_LOAN,
      MovementType.INTEREST,
    ],
  },
  {
    section_name: "revenue",
    fields: [MovementType.DIVIDEND, MovementType.ACCRUED_INTEREST],
  },
  {
    section_name: "unlisted_asset_funds",
    fields: [
      MovementType.CAPITAL_CALL,
      MovementType.CAPITAL_GAIN,
      MovementType.DISTRIBUTION,
      MovementType.CALL_SUBSEQUENT_CLOSING,
      MovementType.TEMPORARY_RETURN_CAPITAL,
      MovementType.DISTRIBUTION_SUBSEQUENT_CLOSING,
      MovementType.RETURN_EXCESS_CALL,
      MovementType.RETURN_EXCESS_DISTRIBUTION,
      MovementType.TRANSFER_FUND_REALLOCATION,
      MovementType.RETURN_EXCESS_TEMPORARY_DISTRIBUTION,
      MovementType.FEEDER_FEE,
    ],
  },
];

export type movementMap = {
  security: Record<string, MovementType[] | Record<string, MovementType[]>>;
  cash: Record<string, MovementType[]>;
  cash_special_case: Record<string, MovementType[]>;
};

export const DEFAULT_MOVEMENT_MAP: movementMap = {
  security: {
    private_equity_fund: {
      pos: [
        MovementType.CAPITAL_CALL,
        MovementType.CALL_SUBSEQUENT_CLOSING,
        MovementType.RETURN_EXCESS_DISTRIBUTION,
        MovementType.TRANSFER_FUND_REALLOCATION,
        MovementType.RETURN_EXCESS_TEMPORARY_DISTRIBUTION,
        MovementType.FEEDER_FEE,
      ],
      neg: [
        MovementType.CAPITAL_GAIN,
        MovementType.DISTRIBUTION,
        MovementType.TEMPORARY_RETURN_CAPITAL,
        MovementType.DISTRIBUTION_SUBSEQUENT_CLOSING,
        MovementType.TRANSFER_FUND_REALLOCATION,
      ],
    },
    shareholder_account: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    real_estate: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    personal_use_real_estate: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    relatives_debt: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    scpi_usufruit: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    vehicle: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
  },
  cash: {
    stock: [MovementType.DIVIDEND],
    equity_fund: [MovementType.DIVIDEND],
    equity_derivative: [MovementType.DIVIDEND],
    stock_option: [MovementType.DIVIDEND],
    other_equity: [MovementType.DIVIDEND],
    cash: [MovementType.ACCRUED_INTEREST],
    monetary_fund: [MovementType.DIVIDEND],
    precious_metal: [MovementType.DIVIDEND],
    other_commodity: [MovementType.DIVIDEND],
    bond: [MovementType.ACCRUED_INTEREST],
    fixed_income_fund: [MovementType.DIVIDEND],
    fixed_income_derivative: [MovementType.DIVIDEND],
    other_fixed_income: [MovementType.DIVIDEND],
    private_equity_direct: [MovementType.DIVIDEND],
    private_debt: [MovementType.ACCRUED_INTEREST],
    hedge_fund: [MovementType.DIVIDEND],
    structured_product: [MovementType.ACCRUED_INTEREST],
    fixed_income_structured_product: [MovementType.ACCRUED_INTEREST],
    shareholder_account: [MovementType.DIVIDEND],
    other_alternative: [MovementType.DIVIDEND],
    home_loan: [MovementType.HOME_LOAN, MovementType.INTEREST],
    other_loan: [MovementType.OTHER_LOAN, MovementType.INTEREST],
  },
  cash_special_case: {
    "*": [MovementType.FEES],
  },
};

export const AUTOMATIC_TAG_MAP: string[] = [
  "private_equity_direct",
  "private_equity_fund",
  "real_estate",
];
